import { useFormikContext } from 'formik';
import type { SetStateAction } from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { REORDER_ACROSS_POD_ERROR } from '../constants';
import { useSequenceContext } from '../context';
import { SequenceAction } from '../context/actions';
import type { TransientSequenceError } from '../context/state';
import type { SequenceAdV5, SequenceFormValues } from '../types';
import { getInitialSequence } from '../utils/initialValues';
import { getPodAlignmentRank, getRotatedSequences, getRotatedUnsequencedAds, getSortedUnsequencedAds } from './utils';
import type { RowError, ValidationResult } from './validation';
import { canReorderAcrossPods, getPodPositionIndex, validateSequences } from './validation';

export const MIN_NAME_LENGTH_ERROR = "Sequence's name should be at least 1 character long!";

export type UseSequenceForm = {
  isControlButtonsDisabled: boolean;
  hasRowValidationErrors: boolean;
  hasSequenceLevelErrors: boolean;
  handleReset: () => void;
  values: SequenceFormValues;
  deleteSequence: (sequenceIndex: number) => void;
  updateSequenceName: (newValue: string, sequenceIndex: number) => string | null;
  changeAdSequenceOrder: (props: ChangeOrderProps) => void;
  movePodAd: (props: MovePodAdProps) => void;
  addSequence: () => void;
  setValues: (values: SetStateAction<SequenceFormValues>, shouldValidate?: boolean | undefined) => void;
  status?: ValidationResult | null;
  rowErrors: RowError[];
  transientSequenceError: TransientSequenceError | null;
};

export type ChangeOrderProps = {
  destination: {
    droppableId: string;
    index: number;
  };
  source: {
    droppableId: string;
    index: number;
  };
  draggedAd: SequenceAdV5;
};

export type MovePodAdProps = {
  seqIndex: number;
  adId: string;
  destPodPosition: string;
  destAlignment: string;
  destIndex: number;
};

const useSequenceForm = (): UseSequenceForm => {
  const flags = useFeatureFlags();
  const allowStandaloneLines = !!flags?.adsLiveDaiStandaloneLineSequences;
  const { values, dirty, isSubmitting, setValues, handleReset, setStatus } = useFormikContext<SequenceFormValues>();
  const {
    sequenceState: { transientSequenceError },
    sequenceDispatch,
  } = useSequenceContext();

  const sequences = values?.sequences;
  const liveValidation: ValidationResult | null = useMemo(() => {
    if (!sequences) return null;
    return validateSequences(sequences, allowStandaloneLines);
  }, [allowStandaloneLines, sequences]);

  const rowErrors: RowError[] = liveValidation?.rowErrors ?? [];
  const hasRowValidationErrors = rowErrors.length > 0;

  const hasSequenceLevelErrors = useMemo(
    () => Object.values(liveValidation?.errorsWithSequenceIds ?? {}).some((set) => set.size > 0),
    [liveValidation]
  );

  // Clear any transient sequence error whenever liveValidation recomputes (sequences changed)
  // or when the user attempts to save. Validation regenerating is the signal that the form has
  // moved past the blocked drop; submitting indicates the user is committing the current state.
  useEffect(() => {
    if (transientSequenceError) {
      sequenceDispatch({ type: SequenceAction.SET_TRANSIENT_SEQUENCE_ERROR, payload: null });
    }
    // We intentionally only depend on liveValidation and isSubmitting; transientSequenceError is
    // read for the guard but should not retrigger this effect on its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveValidation, isSubmitting]);

  const deleteSequence = (sequenceIndex: number): void => {
    setValues((prev) => {
      // get ads from sequence that will be added to unsequenced table
      const deletedSequencedAds = prev.sequences[sequenceIndex]?.ads || [];
      const updatedUnsequencedAds = [...prev.unsequencedAds, ...deletedSequencedAds];

      const sequences = [...prev.sequences];

      sequences.splice(sequenceIndex, 1);

      return {
        ...prev,
        sequences,
        unsequencedAds: getSortedUnsequencedAds(updatedUnsequencedAds),
      };
    });
  };

  const updateSequenceName = (newValue: string, sequenceIndex: number): string | null => {
    // validation on name length
    const error = newValue.length ? null : MIN_NAME_LENGTH_ERROR;

    if (!error) {
      setValues((prev) => {
        const sequences = [...prev.sequences];

        sequences.splice(sequenceIndex, 1, { ...sequences[sequenceIndex], name: newValue });

        return {
          ...prev,
          sequences,
        };
      });
    }

    return error;
  };

  const changeAdSequenceOrder = useCallback(
    (props: ChangeOrderProps): void => {
      const { sequences } = values;

      const { source, destination } = props;
      const sourceSequenceIndex = Number(source.droppableId);
      const destSequenceIndex = Number(destination.droppableId);

      if (sourceSequenceIndex === destSequenceIndex && sourceSequenceIndex >= 0 && sequences[sourceSequenceIndex]) {
        const sequenceAds = sequences[sourceSequenceIndex].ads;
        if (!canReorderAcrossPods(sequenceAds, source.index, destination.index)) {
          const sourceSequenceId = sequences[sourceSequenceIndex].id;
          const isAlreadySurfaced = !!liveValidation?.errorsWithSequenceIds[REORDER_ACROSS_POD_ERROR]?.has(
            sourceSequenceId
          );

          if (!isAlreadySurfaced) {
            sequenceDispatch({
              type: SequenceAction.SET_TRANSIENT_SEQUENCE_ERROR,
              payload: {
                sequenceId: sourceSequenceId,
                message: REORDER_ACROSS_POD_ERROR,
              },
            });
          }
          return;
        }
      }

      // rotate ad sequences in tables and save
      setValues((prev) => {
        const updatedUnsequencedAds = getRotatedUnsequencedAds({
          unsequencedAds: prev.unsequencedAds,
          ...props,
        });

        const updatedSequences = getRotatedSequences({
          sequences: prev.sequences,
          ...props,
        });

        return {
          ...prev,
          sequences: [...updatedSequences],
          unsequencedAds: [...updatedUnsequencedAds],
        };
      });
    },
    [values, setValues, sequenceDispatch, liveValidation]
  );

  // moves an ad within a sequence's pod-preview grid: handles same-group reorder
  // and cross-alignment / cross-pod-position drops in a single state update.
  // Order in the underlying ads array drives both visible position and saved order.
  const movePodAd = ({ seqIndex, adId, destPodPosition, destAlignment, destIndex }: MovePodAdProps): void => {
    setValues((prev) => {
      const sequence = prev.sequences[seqIndex];
      if (!sequence) return prev;

      const fromIndex = sequence.ads.findIndex((ad) => ad.id === adId);
      if (fromIndex < 0) return prev;

      const ads = [...sequence.ads];
      const [draggedAd] = ads.splice(fromIndex, 1);

      const updatedAd: SequenceAdV5 = {
        ...draggedAd,
        adSequence: draggedAd.adSequence
          ? { ...draggedAd.adSequence, podPosition: destPodPosition, podAlignment: destAlignment }
          : draggedAd.adSequence,
      };

      // map the in-group destination index to an absolute index in the ads array
      const destGroupIndices = ads.reduce<number[]>((acc, ad, i) => {
        if (ad.adSequence?.podPosition === destPodPosition && ad.adSequence?.podAlignment === destAlignment) {
          acc.push(i);
        }
        return acc;
      }, []);

      let insertAt: number;
      if (destIndex < destGroupIndices.length) {
        insertAt = destGroupIndices[destIndex];
      } else if (destGroupIndices.length > 0) {
        insertAt = destGroupIndices[destGroupIndices.length - 1] + 1;
      } else {
        // No ads in destPodPosition+destAlignment yet, but the pod may hold ads of the
        // other alignment. Keep START before END within the pod: drop in front of the
        // first same-pod ad ranked after destAlignment, otherwise after the pod's last ad.
        const destAlignmentRank = getPodAlignmentRank(destAlignment);
        const firstLaterAlignmentIdx = ads.findIndex(
          (ad) =>
            ad.adSequence?.podPosition === destPodPosition &&
            getPodAlignmentRank(ad.adSequence?.podAlignment) > destAlignmentRank
        );
        const samePodLastIndex = ads.reduce<number>((last, ad, i) => {
          return ad.adSequence?.podPosition === destPodPosition ? i : last;
        }, -1);

        if (firstLaterAlignmentIdx >= 0) {
          insertAt = firstLaterAlignmentIdx;
        } else if (samePodLastIndex >= 0) {
          insertAt = samePodLastIndex + 1;
        } else {
          // No ads in this pod at all — insert before the first ad from a later pod.
          const destPodIdx = getPodPositionIndex(destPodPosition);
          const firstLaterPodIdx = ads.findIndex((ad) => getPodPositionIndex(ad.adSequence?.podPosition) > destPodIdx);
          insertAt = firstLaterPodIdx >= 0 ? firstLaterPodIdx : ads.length;
        }
      }

      ads.splice(insertAt, 0, updatedAd);

      const sequences = [...prev.sequences];
      sequences[seqIndex] = { ...sequence, ads };
      return { ...prev, sequences };
    });
  };

  const addSequence = (): void => {
    setValues((prev) => {
      const sequences = [...prev.sequences, getInitialSequence(prev.sequences.length + 1)];

      return {
        ...prev,
        sequences,
      };
    });
  };

  // reset status errors, when form is not dirty
  useEffect(() => {
    if (!dirty) {
      setStatus(null);
    }
  }, [dirty, setStatus]);

  return {
    isControlButtonsDisabled: !dirty,
    hasRowValidationErrors,
    hasSequenceLevelErrors,
    handleReset,
    values,
    status: liveValidation,
    rowErrors,
    transientSequenceError,
    setValues,
    deleteSequence,
    updateSequenceName,
    changeAdSequenceOrder,
    movePodAd,
    addSequence,
  };
};

export default useSequenceForm;
